import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Per-panel layout "pin" (lock) for the `/map` page. Pinning a panel freezes
 * its LAYOUT — position, size, dock side, open/visible state, and (for the
 * machine rail) card order — against data- and selection-driven changes. It
 * never freezes the DATA: displayed values, the live-followed subject, video,
 * and the streaming timeline all keep updating in place.
 *
 * Each panel owns its pin independently (no master "pin all"). Persisted so the
 * choice survives reloads.
 */
export type MapPanelKey = "machineRail" | "alertCard" | "eventCard" | "video" | "timeline";

const INITIAL_PINNED: Record<MapPanelKey, boolean> = {
  machineRail: false,
  alertCard: false,
  eventCard: false,
  video: false,
  timeline: false,
};

interface MapPinState {
  pinned: Record<MapPanelKey, boolean>;
  isPinned: (key: MapPanelKey) => boolean;
  togglePin: (key: MapPanelKey) => void;
}

export const useMapPinStore = create<MapPinState>()(
  persist(
    (set, get) => ({
      pinned: INITIAL_PINNED,
      isPinned: (key) => !!get().pinned[key],
      togglePin: (key) =>
        set((state) => ({ pinned: { ...state.pinned, [key]: !state.pinned[key] } })),
    }),
    { name: "map-pin-state" },
  ),
);
